import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  Chip,
  Alert,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { MethodEditorProps } from '../types';
import { FORM_FIELD_EXAMPLES } from '../constants';
import { PresetBatchEditor } from '../components/PresetBatchEditor';
import { GenerativeBatchEditor } from '../components/GenerativeBatchEditor';

const generateBatchId = () => `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const BatchGenerateEditor: React.FC<MethodEditorProps> = ({ payload, onChange }) => {
  const [newContextMapSource, setNewContextMapSource] = useState('');
  const [newContextMapTarget, setNewContextMapTarget] = useState('');
  const [newOutputMapSource, setNewOutputMapSource] = useState('');
  const [newOutputMapTarget, setNewOutputMapTarget] = useState('');

  const addBatch = (type: 'preset' | 'generative') => {
    const newBatch =
      type === 'preset'
        ? { id: generateBatchId(), type: 'preset', fields: {} }
        : {
            id: generateBatchId(),
            type: 'generative',
            target: 'form',
            generationId: '',
            contextToPromptMap: [],
            outputToFieldMap: [],
          };

    onChange({
      ...payload,
      batches: [...(payload.batches || []), newBatch],
    });
  };

  const removeBatch = (index: number) => {
    const newBatches = [...(payload.batches || [])];
    newBatches.splice(index, 1);
    onChange({ ...payload, batches: newBatches });
  };

  const updateBatch = (index: number, updatedBatch: any) => {
    const newBatches = [...(payload.batches || [])];
    newBatches[index] = updatedBatch;
    onChange({ ...payload, batches: newBatches });
  };

  const addContextMapping = (batchIndex: number) => {
    if (!newContextMapSource || !newContextMapTarget) {
      return;
    }

    const batch = payload.batches[batchIndex];
    const newMapping = {
      id: generateBatchId(),
      source: newContextMapSource,
      target: newContextMapTarget,
    };

    updateBatch(batchIndex, {
      ...batch,
      contextToPromptMap: [...(batch.contextToPromptMap || []), newMapping],
    });

    setNewContextMapSource('');
    setNewContextMapTarget('');
  };

  const removeContextMapping = (batchIndex: number, mappingIndex: number) => {
    const batch = payload.batches[batchIndex];
    const newMappings = [...(batch.contextToPromptMap || [])];
    newMappings.splice(mappingIndex, 1);

    updateBatch(batchIndex, {
      ...batch,
      contextToPromptMap: newMappings,
    });
  };

  const addOutputMapping = (batchIndex: number) => {
    if (!newOutputMapSource || !newOutputMapTarget) {
      return;
    }

    const batch = payload.batches[batchIndex];
    const newMapping = {
      id: generateBatchId(),
      source: newOutputMapSource,
      target: newOutputMapTarget,
    };

    updateBatch(batchIndex, {
      ...batch,
      outputToFieldMap: [...(batch.outputToFieldMap || []), newMapping],
    });

    setNewOutputMapSource('');
    setNewOutputMapTarget('');
  };

  const removeOutputMapping = (batchIndex: number, mappingIndex: number) => {
    const batch = payload.batches[batchIndex];
    const newMappings = [...(batch.outputToFieldMap || [])];
    newMappings.splice(mappingIndex, 1);

    updateBatch(batchIndex, {
      ...batch,
      outputToFieldMap: newMappings,
    });
  };

  return (
    <Box>
      {/* Form Selection */}
      <FormControl fullWidth margin="normal">
        <InputLabel id="target-form-label">Target Form</InputLabel>
        <Select
          labelId="target-form-label"
          label="Target Form"
          value={payload?.form || ''}
          onChange={(e) => onChange({ ...payload, form: e.target.value })}
        >
          {FORM_FIELD_EXAMPLES.map((form) => (
            <MenuItem key={form} value={form}>
              {form}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
        <Typography variant="body2">
          <strong>Batch Generation:</strong> Execute multiple operations in sequence
          <br />• <strong>Preset batches:</strong> Set fields immediately with static values
          <br />• <strong>Generative batches:</strong> Use AI to generate content dynamically
        </Typography>
      </Alert>

      {/* Batches */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Batches</Typography>
        <Box display="flex" gap={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => addBatch('preset')}
            startIcon={<AddIcon />}
          >
            Add Preset Batch
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => addBatch('generative')}
            startIcon={<AddIcon />}
          >
            Add Generative Batch
          </Button>
        </Box>
      </Box>

      {/* Batch List */}
      {(payload.batches || []).map((batch: any, index: number) => (
        <Accordion key={batch.id || `batch-${index}`} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={2} width="100%">
              <Chip
                label={batch.type}
                color={batch.type === 'preset' ? 'primary' : 'secondary'}
                size="small"
              />
              <Typography variant="subtitle1">
                {batch.type === 'preset'
                  ? `Preset Batch ${index + 1}`
                  : `Generative Batch ${index + 1} (${batch.generationId || 'No ID'})`}
              </Typography>
              <Box flex={1} />
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  removeBatch(index);
                }}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {batch.type === 'preset' ? (
              <PresetBatchEditor
                batch={batch}
                onChange={(updatedBatch) => updateBatch(index, updatedBatch)}
              />
            ) : (
              <GenerativeBatchEditor
                batch={batch}
                batchIndex={index}
                onChange={(updatedBatch) => updateBatch(index, updatedBatch)}
                onAddContextMapping={() => addContextMapping(index)}
                onRemoveContextMapping={(mappingIndex) => removeContextMapping(index, mappingIndex)}
                onAddOutputMapping={() => addOutputMapping(index)}
                onRemoveOutputMapping={(mappingIndex) => removeOutputMapping(index, mappingIndex)}
                newContextMapSource={newContextMapSource}
                setNewContextMapSource={setNewContextMapSource}
                newContextMapTarget={newContextMapTarget}
                setNewContextMapTarget={setNewContextMapTarget}
                newOutputMapSource={newOutputMapSource}
                setNewOutputMapSource={setNewOutputMapSource}
                newOutputMapTarget={newOutputMapTarget}
                setNewOutputMapTarget={setNewOutputMapTarget}
              />
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {(payload.batches || []).length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">
            No batches configured. Add a preset or generative batch to get started.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};
